import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  X, CreditCard, Receipt, QrCode, ExternalLink, Copy, CheckCircle2, AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient } from '@/services/api';
import { formatCents, cn } from '@/utils/cn';

type PaymentMethod = 'pix' | 'boleto' | 'credit_card';

interface Invoice {
  id: number;
  invoice_number: string;
  total_cents: number;
  status: string;
  due_date: string;
  asaas_invoice_url?: string | null;
  boleto_url?: string | null;
  pix_copy_paste?: string | null;
  payment_method?: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice: Invoice;
}

interface RegenerateResponse {
  invoice_id: number;
  asaas_payment_id: string;
  invoice_url: string;
  boleto_url: string;
  pix_copy_paste: string;
  payment_method: string;
}

const PAYMENT_OPTIONS: { value: PaymentMethod; label: string; sub: string; icon: typeof QrCode }[] = [
  { value: 'pix', label: 'PIX', sub: 'Pagamento instantâneo', icon: QrCode },
  { value: 'boleto', label: 'Boleto', sub: 'Compensação 1-2 dias', icon: Receipt },
  { value: 'credit_card', label: 'Cartão', sub: 'Cobrança imediata', icon: CreditCard },
];

/**
 * Modal pra pagar uma invoice específica (pendente ou atrasada).
 * - Se a invoice já tem URL/PIX válido, mostra direto.
 * - Senão (ou se cliente trocou de método), gera nova cobrança via /regenerate.
 */
export function PayInvoiceModal({ open, onOpenChange, invoice }: Props) {
  const qc = useQueryClient();
  const [method, setMethod] = useState<PaymentMethod>(
    (invoice.payment_method as PaymentMethod) || 'pix'
  );
  const [copied, setCopied] = useState(false);
  const [regenResult, setRegenResult] = useState<RegenerateResponse | null>(null);

  const regenMutation = useMutation({
    mutationFn: async () => {
      const res = await apiClient.regenerateInvoice(invoice.id, method);
      return res.data as RegenerateResponse;
    },
    onSuccess: (data) => {
      setRegenResult(data);
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['subscription'] });
    },
  });

  // Dados efetivos: resultado da regeneração (se houve), senão dados da invoice.
  const invoiceUrl = regenResult?.invoice_url || invoice.asaas_invoice_url || '';
  const boletoUrl = regenResult?.boleto_url || invoice.boleto_url || '';
  const pixCopyPaste = regenResult?.pix_copy_paste || invoice.pix_copy_paste || '';
  const hasPaymentReady =
    (method === 'pix' && !!pixCopyPaste) ||
    (method !== 'pix' && (!!invoiceUrl || !!boletoUrl));

  // Se o método selecionado é diferente do método da invoice ou nada está pronto,
  // o usuário precisa clicar "Gerar nova cobrança".
  const methodChanged = !regenResult && method !== ((invoice.payment_method as PaymentMethod) || 'pix');
  const needsRegen = methodChanged || !hasPaymentReady;

  async function copyPix() {
    if (!pixCopyPaste) return;
    await navigator.clipboard.writeText(pixCopyPaste);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function handleClose() {
    setRegenResult(null);
    setCopied(false);
    regenMutation.reset();
    onOpenChange(false);
  }

  return (
    <Dialog.Root open={open} onOpenChange={(o) => { if (!o) handleClose(); else onOpenChange(true); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-background p-0 shadow-2xl">
          {/* Header */}
          <div className="gradient-brand text-white px-6 py-5 rounded-t-2xl relative overflow-hidden">
            <div aria-hidden className="absolute inset-0 opacity-30" style={{ background: 'radial-gradient(600px 100px at 50% 0%, rgba(255,255,255,.35), transparent)' }} />
            <div className="flex items-start justify-between relative">
              <div>
                <Dialog.Title className="font-display text-xl font-bold tracking-tight">
                  Pagar fatura
                </Dialog.Title>
                <Dialog.Description className="text-sm text-white/90 mt-1">
                  {invoice.invoice_number} · <strong>{formatCents(invoice.total_cents)}</strong>
                </Dialog.Description>
              </div>
              <Dialog.Close asChild>
                <button className="rounded-md p-1 hover:bg-white/20 text-white" aria-label="Fechar">
                  <X className="h-5 w-5" />
                </button>
              </Dialog.Close>
            </div>
          </div>

          <div className="p-6">
            {/* Alerta se overdue */}
            {invoice.status === 'overdue' && (
              <div className="mb-4 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700 flex items-start gap-2">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <div>
                  Esta fatura está <strong>vencida</strong>. Pague agora pra evitar suspensão do serviço.
                </div>
              </div>
            )}

            {/* Seletor de forma de pagamento */}
            <div className="mb-5">
              <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                Forma de pagamento
              </div>
              <div className="grid grid-cols-3 gap-2">
                {PAYMENT_OPTIONS.map(opt => {
                  const Icon = opt.icon;
                  const selected = method === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => { setMethod(opt.value); setRegenResult(null); }}
                      className={cn(
                        'flex flex-col items-center gap-1 rounded-lg border-2 p-3 transition-colors',
                        selected
                          ? 'border-brand-600 bg-brand-50/40'
                          : 'border-border hover:border-muted-foreground/30',
                      )}
                    >
                      <Icon className={cn('h-5 w-5', selected ? 'text-brand-600' : 'text-muted-foreground')} />
                      <div className="text-xs font-semibold">{opt.label}</div>
                      <div className="text-[10px] text-muted-foreground text-center leading-tight">{opt.sub}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Conteúdo principal: ou mostra pagamento pronto, ou pede regenerar */}
            {needsRegen ? (
              <div className="text-center py-4">
                <p className="text-sm text-muted-foreground mb-4">
                  {methodChanged
                    ? 'Vamos gerar uma nova cobrança com a forma de pagamento escolhida.'
                    : 'Esta fatura ainda não tem cobrança ativa no gateway. Clique abaixo pra gerar.'}
                </p>
                <Button
                  variant="gradient"
                  className="btn-gradient-glow w-full"
                  loading={regenMutation.isPending}
                  onClick={() => regenMutation.mutate()}
                >
                  Gerar cobrança {method === 'pix' ? 'PIX' : method === 'boleto' ? 'boleto' : 'cartão'}
                </Button>
              </div>
            ) : (
              <>
                {/* PIX copia-e-cola */}
                {method === 'pix' && pixCopyPaste && (
                  <div className="rounded-xl border-2 border-border p-4 mb-4">
                    <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                      PIX copia-e-cola
                    </div>
                    <div className="font-mono text-xs bg-muted p-3 rounded break-all max-h-32 overflow-y-auto">
                      {pixCopyPaste}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3 w-full"
                      onClick={copyPix}
                    >
                      {copied ? (
                        <><CheckCircle2 className="h-4 w-4 text-emerald-500" /> Copiado!</>
                      ) : (
                        <><Copy className="h-4 w-4" /> Copiar código</>
                      )}
                    </Button>
                  </div>
                )}

                {/* Link genérico do Asaas */}
                {(invoiceUrl || boletoUrl) && (
                  <Button asChild variant="gradient" className="btn-gradient-glow w-full mb-3">
                    <a href={invoiceUrl || boletoUrl} target="_blank" rel="noopener noreferrer">
                      Abrir página de pagamento <ExternalLink className="h-4 w-4" />
                    </a>
                  </Button>
                )}
              </>
            )}

            {regenMutation.isError && (
              <div className="mt-3 text-sm text-red-600 text-center">
                Erro ao gerar nova cobrança. Tente outra forma de pagamento.
              </div>
            )}

            <Button variant="outline" className="w-full mt-3" onClick={handleClose}>
              Fechar
            </Button>

            <p className="text-xs text-muted-foreground text-center mt-3">
              Assim que o pagamento for confirmado pelo banco, sua conta volta ao ar automaticamente.
            </p>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
